import React from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  alpha,
  Grid,
  Tooltip,
  Zoom
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  ReferenceLine,
  ReferenceArea
} from 'recharts';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TimelineIcon from '@mui/icons-material/Timeline';

const SpeedProfileChart = ({ 
  trajectory, 
  speedProfile, 
  fps = 30,
  height = 300 
}) => {
  const theme = useTheme();
  
  // Prepare chart data
  const prepareChartData = () => {
    if (!trajectory || trajectory.length === 0) {
      return [];
    }
    
    return trajectory.map((point, index) => {
      const speed = speedProfile && speedProfile[index] !== undefined 
        ? speedProfile[index] 
        : calculateSpeedAtPoint(index);
      
      const acceleration = index > 0 ? calculateAcceleration(index) : 0;
      
      return {
        frame: index,
        time: point.timestamp || (index / fps),
        distance: point.x || 0,
        height: point.y || 0,
        speed: speed,
        acceleration: acceleration,
        speedCategory: getSpeedCategory(speed)
      };
    });
  };
  
  const calculateSpeedAtPoint = (index) => {
    if (index === 0 || index >= trajectory.length - 1) return 0;
    
    const prev = trajectory[index - 1];
    const curr = trajectory[index];
    const next = trajectory[index + 1];
    
    // Calculate speed using finite difference
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    const dt = 2 / fps; // Two frames difference
    
    const velocity = Math.sqrt((dx * dx) + (dy * dy)) / dt;
    return velocity * 3.6; // Convert m/s to km/h
  };
  
  const calculateAcceleration = (index) => {
    if (index < 2 || index >= trajectory.length - 2) return 0;
    
    const speeds = [
      calculateSpeedAtPoint(index - 2),
      calculateSpeedAtPoint(index - 1),
      calculateSpeedAtPoint(index),
      calculateSpeedAtPoint(index + 1),
      calculateSpeedAtPoint(index + 2)
    ];
    
    // Calculate acceleration using central difference
    const avgAcceleration = (speeds[4] - speeds[0]) / (4 / fps);
    return avgAcceleration / 3.6; // Convert km/h/s to m/s²
  };
  
  const getSpeedCategory = (speed) => {
    if (speed > 140) return 'very_fast';
    if (speed > 130) return 'fast';
    if (speed > 115) return 'medium_fast';
    if (speed > 100) return 'medium';
    return 'slow';
  };
  
  const chartData = prepareChartData();
  
  // Calculate statistics
  const calculateStats = () => {
    if (chartData.length === 0) {
      return {
        avgSpeed: 0,
        maxSpeed: 0,
        minSpeed: 0,
        speedLoss: 0,
        releaseSpeed: 0,
        impactSpeed: 0,
        avgAcceleration: 0
      };
    }
    
    const speeds = chartData.map(d => d.speed).filter(s => s > 0);
    const accelerations = chartData.map(d => d.acceleration).filter(a => !isNaN(a));
    
    const avgSpeed = speeds.length > 0 ? speeds.reduce((a, b) => a + b, 0) / speeds.length : 0;
    const maxSpeed = speeds.length > 0 ? Math.max(...speeds) : 0;
    const minSpeed = speeds.length > 0 ? Math.min(...speeds) : 0;
    const releaseSpeed = speeds.length > 0 ? speeds[0] : 0;
    const impactSpeed = speeds.length > 0 ? speeds[speeds.length - 1] : 0;
    const speedLoss = releaseSpeed > 0 ? ((releaseSpeed - impactSpeed) / releaseSpeed) * 100 : 0;
    const avgAcceleration = accelerations.length > 0 
      ? accelerations.reduce((a, b) => a + b, 0) / accelerations.length 
      : 0;
    
    return {
      avgSpeed,
      maxSpeed,
      minSpeed,
      speedLoss,
      releaseSpeed,
      impactSpeed,
      avgAcceleration
    };
  };
  
  const stats = calculateStats();
  
  // Find key points in the trajectory
  const findKeyPoints = () => {
    if (chartData.length === 0) return {};
    
    const releasePoint = chartData[0];
    const maxSpeedPoint = chartData.reduce((max, point) => 
      point.speed > max.speed ? point : max, chartData[0]
    );
    const minSpeedPoint = chartData.reduce((min, point) => 
      point.speed < min.speed ? point : min, chartData[0]
    );
    
    // Find pitch impact (lowest point)
    const impactPoint = chartData.reduce((min, point) => 
      point.height < min.height ? point : min, chartData[0]
    );
    
    return {
      release: releasePoint,
      maxSpeed: maxSpeedPoint,
      minSpeed: minSpeedPoint,
      impact: impactPoint
    };
  };
  
  const keyPoints = findKeyPoints();
  
  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ 
          p: 2, 
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Frame: {data.frame}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            Time: {data.time.toFixed(2)}s
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            Distance: {data.distance.toFixed(2)}m
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            Height: {data.height.toFixed(2)}m
          </Typography>
          <Typography variant="caption" display="block" color="primary">
            Speed: {data.speed.toFixed(1)} km/h
          </Typography>
          {data.acceleration && (
            <Typography variant="caption" display="block" 
              color={data.acceleration >= 0 ? 'success.main' : 'error.main'}>
              Acceleration: {data.acceleration.toFixed(2)} m/s²
            </Typography>
          )}
        </Paper>
      );
    }
    return null;
  };
  
  // Get speed gradient color
  const getSpeedColor = (speed) => {
    if (speed > 140) return theme.palette.error.main;
    if (speed > 130) return theme.palette.warning.main;
    if (speed > 115) return theme.palette.info.main;
    return theme.palette.success.main;
  };
  
  // Render speed zones
  const renderSpeedZones = () => {
    const zones = [
      { name: 'Very Fast', min: 140, max: 160, color: alpha(theme.palette.error.main, 0.1) },
      { name: 'Fast', min: 130, max: 140, color: alpha(theme.palette.warning.main, 0.1) },
      { name: 'Medium Fast', min: 115, max: 130, color: alpha(theme.palette.info.main, 0.1) },
      { name: 'Medium', min: 100, max: 115, color: alpha(theme.palette.success.main, 0.1) },
      { name: 'Slow', min: 0, max: 100, color: alpha(theme.palette.text.secondary, 0.05) }
    ];
    
    return zones.map((zone, index) => (
      <ReferenceArea
        key={zone.name}
        y1={zone.min}
        y2={zone.max}
        fill={zone.color}
        stroke="none"
      />
    ));
  };
  
  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SpeedIcon /> Speed Profile Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Speed variation along the ball's trajectory
        </Typography>
      </Box>
      
      {/* Key Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Tooltip title="Average speed throughout the delivery" TransitionComponent={Zoom}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Avg Speed
              </Typography>
              <Typography variant="h5" sx={{ 
                color: getSpeedColor(stats.avgSpeed),
                fontWeight: 600 
              }}>
                {stats.avgSpeed.toFixed(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                km/h
              </Typography>
            </Box>
          </Tooltip>
        </Grid>
        
        <Grid item xs={6} md={3}>
          <Tooltip title="Maximum speed achieved" TransitionComponent={Zoom}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Max Speed
              </Typography>
              <Typography variant="h5" sx={{ 
                color: getSpeedColor(stats.maxSpeed),
                fontWeight: 600 
              }}>
                {stats.maxSpeed.toFixed(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                km/h
              </Typography>
            </Box>
          </Tooltip>
        </Grid>
        
        <Grid item xs={6} md={3}>
          <Tooltip title="Speed loss from release to impact" TransitionComponent={Zoom}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Speed Loss
              </Typography>
              <Typography variant="h5" sx={{ 
                color: stats.speedLoss > 10 ? theme.palette.error.main : theme.palette.success.main,
                fontWeight: 600 
              }}>
                {stats.speedLoss.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(stats.releaseSpeed - stats.impactSpeed).toFixed(1)} km/h
              </Typography>
            </Box>
          </Tooltip>
        </Grid>
        
        <Grid item xs={6} md={3}>
          <Tooltip title="Average acceleration/deceleration" TransitionComponent={Zoom}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Avg Acceleration
              </Typography>
              <Typography variant="h5" sx={{ 
                color: stats.avgAcceleration >= 0 ? theme.palette.success.main : theme.palette.error.main,
                fontWeight: 600 
              }}>
                {stats.avgAcceleration.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                m/s²
              </Typography>
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
      
      {/* Main Chart */}
      <Box sx={{ height: height, mb: 3 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={alpha(theme.palette.divider, 0.3)} 
            />
            
            {/* Speed zones */}
            {renderSpeedZones()}
            
            <XAxis
              dataKey="time"
              label={{ 
                value: 'Time (seconds)', 
                position: 'insideBottom', 
                offset: -5 
              }}
              stroke={theme.palette.text.secondary}
              tick={{ fill: theme.palette.text.secondary }}
            />
            <YAxis
              label={{ 
                value: 'Speed (km/h)', 
                angle: -90, 
                position: 'insideLeft' 
              }}
              stroke={theme.palette.text.secondary}
              tick={{ fill: theme.palette.text.secondary }}
              domain={[0, 160]}
            />
            
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend />
            
            {/* Reference lines for key points */}
            {keyPoints.release && (
              <ReferenceLine
                x={keyPoints.release.time}
                stroke={theme.palette.success.main}
                strokeDasharray="3 3"
                label={{ 
                  value: 'Release', 
                  position: 'top',
                  fill: theme.palette.success.main 
                }}
              />
            )}
            
            {keyPoints.maxSpeed && (
              <ReferenceLine
                x={keyPoints.maxSpeed.time}
                stroke={theme.palette.warning.main}
                strokeDasharray="3 3"
                label={{ 
                  value: 'Max Speed', 
                  position: 'top',
                  fill: theme.palette.warning.main 
                }}
              />
            )}
            
            {keyPoints.impact && (
              <ReferenceLine
                x={keyPoints.impact.time}
                stroke={theme.palette.error.main}
                strokeDasharray="3 3"
                label={{ 
                  value: 'Impact', 
                  position: 'bottom',
                  fill: theme.palette.error.main 
                }}
              />
            )}
            
            {/* Speed area chart */}
            <Area
              type="monotone"
              dataKey="speed"
              name="Speed (km/h)"
              stroke={theme.palette.primary.main}
              fill={alpha(theme.palette.primary.main, 0.3)}
              fillOpacity={0.6}
              strokeWidth={2}
              dot={false}
              activeDot={{ 
                r: 6, 
                fill: theme.palette.primary.main,
                stroke: theme.palette.background.paper,
                strokeWidth: 2 
              }}
            />
            
            {/* Acceleration line */}
            <Line
              type="monotone"
              dataKey="acceleration"
              name="Acceleration (m/s²)"
              stroke={theme.palette.secondary.main}
              strokeWidth={1.5}
              strokeDasharray="3 3"
              dot={false}
              activeDot={{ r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
      
      {/* Key Points Information */}
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom color="text.secondary">
          Key Speed Points
        </Typography>
        <Grid container spacing={2}>
          {keyPoints.release && (
            <Grid item xs={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: theme.palette.success.main 
                }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Release
                  </Typography>
                  <Typography variant="body2">
                    {keyPoints.release.speed.toFixed(1)} km/h
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
          
          {keyPoints.maxSpeed && (
            <Grid item xs={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: theme.palette.warning.main 
                }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Max Speed
                  </Typography>
                  <Typography variant="body2">
                    {keyPoints.maxSpeed.speed.toFixed(1)} km/h
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
          
          {keyPoints.impact && (
            <Grid item xs={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: theme.palette.error.main 
                }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Impact
                  </Typography>
                  <Typography variant="body2">
                    {keyPoints.impact.speed.toFixed(1)} km/h
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
          
          <Grid item xs={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {stats.avgAcceleration >= 0 ? (
                <TrendingUpIcon color="success" fontSize="small" />
              ) : (
                <TrendingDownIcon color="error" fontSize="small" />
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Trend
                </Typography>
                <Typography variant="body2">
                  {stats.avgAcceleration >= 0 ? 'Accelerating' : 'Decelerating'}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Speed Categories Legend */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
        {[
          { label: 'Very Fast (>140)', color: theme.palette.error.main },
          { label: 'Fast (130-140)', color: theme.palette.warning.main },
          { label: 'Medium Fast (115-130)', color: theme.palette.info.main },
          { label: 'Medium (100-115)', color: theme.palette.success.main },
          { label: 'Slow (<100)', color: theme.palette.text.secondary }
        ].map((category) => (
          <Box key={category.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ 
              width: 10, 
              height: 10, 
              borderRadius: '50%', 
              backgroundColor: category.color 
            }} />
            <Typography variant="caption" color="text.secondary">
              {category.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default SpeedProfileChart;